/**
 * Maps UI routes to documentation paths.
 * Entries are matched top-down by prefix — more specific paths must come first.
 */
const routeToDocsPath: [prefix: string, docsPath: string][] = [
  // Dashboard
  ['/dashboard', '/docs/user/dashboard/overview'],

  // Equipment — Edges (detail routes before list)
  ['/edges/:id/config', '/docs/user/equipment/edges/edge-configuration'],
  ['/edges/:id', '/docs/user/equipment/edges/edge-detail'],
  ['/edges', '/docs/user/equipment/edges/'],

  // Equipment — Shared Gateways
  ['/mtges/:id', '/docs/user/equipment/shared-gateways/'],
  ['/mtges', '/docs/user/equipment/shared-gateways/'],

  // Equipment — Connectors
  ['/connectors/:id', '/docs/user/equipment/connectors/'],
  ['/connectors', '/docs/user/equipment/connectors/'],

  // Equipment — App Devices & Groups
  ['/app-device-groups', '/docs/user/equipment/app-devices/groups'],
  ['/app-devices', '/docs/user/equipment/app-devices/'],

  // Network
  ['/dns', '/docs/user/network/private-dns'],
  ['/edge-peering/:id', '/docs/user/network/edge-peering/'],
  ['/edge-peering', '/docs/user/network/edge-peering/'],
  ['/routing-policies', '/docs/user/network/routing-policies'],
  ['/applications', '/docs/user/network/applications'],

  // Monitoring
  ['/monitoring/status', '/docs/user/monitoring/device-status'],
  ['/monitoring/performance', '/docs/user/monitoring/performance-charts'],
  ['/monitoring/logs', '/docs/user/monitoring/logs'],
  ['/monitoring/diagnostic', '/docs/user/monitoring/diagnostic-tools'],
  ['/monitoring/observability', '/docs/user/monitoring/observability-dashboards'],
  ['/monitoring/acl', '/docs/user/monitoring/acl-monitoring'],
  ['/monitoring/suricata', '/docs/user/monitoring/suricata'],

  // Analytics
  ['/analytics/traffic', '/docs/user/analytics/traffic-analysis'],
  ['/analytics/reports', '/docs/user/analytics/reports'],
  ['/analytics', '/docs/user/analytics/traffic-analysis'],

  // Notifications
  ['/notifications', '/docs/user/notifications'],

  // Administration
  ['/admin/config-templates/:id', '/docs/admin/config-templates'],
  ['/admin/config-templates', '/docs/admin/config-templates'],
  ['/admin/audit-log', '/docs/admin/audit-log'],
  ['/admin/users', '/docs/admin/user-management/admin-users'],
  ['/admin/tenants', '/docs/admin/tenant-management/'],
  ['/admin/roles', '/docs/admin/user-management/roles'],
  ['/admin/system', '/docs/admin/system-settings/general'],

  // Account
  ['/profile/settings', '/docs/user/account/timezone'],
  ['/profile', '/docs/user/account/profile'],
];

/**
 * Convert a route prefix pattern (with optional `:param` segments) into a
 * regex that matches any pathname starting with that pattern.
 */
function prefixToRegex(prefix: string): RegExp {
  // Replace :param segments with a pattern that matches any path segment
  const pattern = prefix.replace(/:[^/]+/g, '[^/]+');
  return new RegExp(`^${pattern}(/|$)`);
}

const compiledRoutes = routeToDocsPath.map(
  ([prefix, docsPath]) => [prefixToRegex(prefix), docsPath] as const,
);

/**
 * Returns the full docs URL for the given UI pathname.
 * Falls back to the docs homepage if no specific match is found.
 */
export function getDocsUrl(pathname: string): string {
  const base = import.meta.env.VITE_DOCS_URL || 'https://docs.vsm.netlinkz.dev';

  for (const [regex, docsPath] of compiledRoutes) {
    if (regex.test(pathname)) {
      return `${base}${docsPath}`;
    }
  }

  // Default: docs homepage
  return base;
}
